import { FunctionComponent, useEffect, useState, useMemo } from "react";
import { SuperAdminAccess } from "./SuperAdminAccess";
import Modal from "../../ui-components/modal";
import {
  NewFlowModal,
  PodEnableModal,
  PublishModal,
  TriggerModal,
  UploadFile,
} from "./modals";
import BotPopup, { PopUpOptions } from "../../ui-components/popup";
import { NoFlow, TableStyle } from "./EntryPoint.style";
import OptionsIcon from "../../icons/options";
import PlusIcon from "../../icons/Plus";
import { homeDataAction } from "../../state/homeState";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { flowMetaStateActions } from "../../state/flowMetaState";
import { SlackNotification } from "../../utilities/slackNotification";
import { useAppSelector } from "../../state/store";
import { userDataState } from "../../state/storeDataStateType";
import { createNewFlow } from "../../services/helpers/StoreHelper";
import { notificationDataStateActions } from "../../state/notificationDataState";
import { Button, Tag } from "@bikdotai/bik-component-library";
import { getLast7DaysStartAndEndDate } from "../../utilities/dateUtils";

interface TableProps {
  items: { [key: string]: any };
  emitFlowName: (flowName: string) => void;
  emitUpdatedFlowList?: (flowList: { [key: string]: any }) => void;
}

const TemplateTable: FunctionComponent<TableProps> = (props) => {
  const [showPopover, setShowPopover] = useState<string>("");
  const [itemList, setItemList] = useState<{ [key: string]: any }>(props.items);
  const [currentId, setCurrentId] = useState<any>();
  const [showModal, setShowModal] = useState<boolean>();
  const [type, setType] = useState<string>("");
  const [errors, setErrors] = useState<{ [name: string]: string }>({});
  const [showNewFlowModal, setShowNewFlowModal] = useState(false);
  const [showEditFlowModal, setShowEditFlowModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [flowLoading, setFlowLoading] = useState<boolean>(false);
  const [selectedFlows, setSelectedFlows] = useState<{
    [key: string]: boolean;
  }>({});
  const [currentSelectedFlows, setCurrentSelectedFlows] = useState<string[]>(
    []
  );
  const [flowIdExistingMap, setFlowIdExistingMap] = useState<{
    [key: string]: string;
  }>({});

  // Filter and search states
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [triggerFilter, setTriggerFilter] = useState<string>("all");

  const storeId = useAppSelector((state) => state.homeState?.storeId);
  const superAdminFns = new SuperAdminAccess();
  let flowData = props.items;
  const navigate = useNavigate();
  const dispatcher = useDispatch();
  const [keywordsList, setKeywordsList] = useState({} as any);

  useEffect(() => {
    const sortedKey = Object.keys(props.items).sort();
    const res: any = {};
    sortedKey.forEach((key) => {
      res[key] = props.items[key];
    });
    const existingFlows: { [key: string]: string } = {};
    let tempKeywordsList: any = {};
    const newSelectedFlows: { [key: string]: boolean } = {};

    Object.keys(res).forEach((key) => {
      let flowData = res[key];

      // Check if keywordsNew exists at the root level
      const keywordsNew = flowData.keywordsNew || {};
      // Check if keywords exist in the separate keywordsNew object
      if (Object.keys(keywordsNew).length > 0) {
        Object.keys(keywordsNew).forEach((channelId) => {
          const keywordsForFlow = keywordsNew[channelId] ?? {};
          tempKeywordsList[key] = []
            .concat(keywordsForFlow.EXACT || [])
            .concat(keywordsForFlow.BEGINS_WITH || [])
            .concat(keywordsForFlow.CONTAINS || [])
            .concat(keywordsForFlow.INCLUDES || [])
            .filter(
              (keyword: any) =>
                keyword && typeof keyword === "string" && keyword.trim() !== ""
            );
        });
      } else {
        // Fallback to old keywords structure in flow data
        tempKeywordsList[key] = flowData?.keywords || [];
      }

      existingFlows[key] = "existing";
      newSelectedFlows[key] = false;
    });
    setFlowIdExistingMap(existingFlows);
    setKeywordsList(tempKeywordsList);
    setSelectedFlows(newSelectedFlows);
    setItemList(res);
  }, [props.items, showModal]);

  // Get unique channel options
  const channelOptions = useMemo(() => {
    const channels = Object.values(itemList)
      .map((item: any) => item.channel)
      .filter(Boolean);
    return ["all", ...Array.from(new Set(channels))];
  }, [itemList]);

  // Get unique status options
  const statusOptions = useMemo(() => {
    return ["all", "enabled", "disabled", "test"];
  }, []);

  // Get unique trigger options
  const triggerOptions = useMemo(() => {
    const triggers = Object.values(itemList)
      .map((item: any) => item.triggerType)
      .filter(Boolean);
    return ["all", ...Array.from(new Set(triggers))];
  }, [itemList]);

  // Filter items based on search and filters
  const filteredItems = useMemo(() => {
    return Object.keys(itemList).filter((key) => {
      const item = itemList[key];

      // Search filter - search in flow name and keywords
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const flowNameMatch = item.flowName
          ?.toLowerCase()
          .includes(searchLower);
        const keywordsMatch = keywordsList[key]?.some((keyword: string) =>
          keyword.toLowerCase().includes(searchLower)
        );

        if (!flowNameMatch && !keywordsMatch) {
          return false;
        }
      }

      // Trigger filter
      if (triggerFilter !== "all" && item.triggerType !== triggerFilter) {
        return false;
      }

      // Channel filter
      if (channelFilter !== "all" && item.channel !== channelFilter) {
        return false;
      }

      // Status filter
      if (statusFilter !== "all") {
        if (statusFilter === "enabled" && !item.status) return false;
        if (statusFilter === "disabled" && (item.status || item.isTestMode))
          return false;
        if (statusFilter === "test" && !item.isTestMode) return false;
      }

      return true;
    });
  }, [
    itemList,
    searchTerm,
    triggerFilter,
    channelFilter,
    statusFilter,
    keywordsList,
  ]);

  const onClickSaveTrigger = async (val: string) => {
    itemList[currentId].trigger = val;
    await superAdminFns.saveFlowMeta(storeId, currentId, itemList);
    setModalTypeAndValue();
  };

  const onClickSaveEnablements = async (val: string[]) => {
    itemList[currentId].enabledPods = val;
    await superAdminFns.saveFlowMeta(storeId, currentId, itemList);
    setModalTypeAndValue();
  };

  const onClickHandle = (id: string, hidePopover = false) => {
    setCurrentId(id);
    if (hidePopover) {
      return;
    }
    if (showPopover === id) {
      setShowPopover("");
    } else {
      setShowPopover(id);
    }
  };

  const setModalTypeAndValue = (val = "") => {
    setShowModal(!showModal);
    setType(val);
  };

  const createFlow = () => {
    if (!storeId) {
      setErrors({
        ...errors,
        value1: "Is Required",
      });
      setItemList({});
      return;
    } else {
      setErrors({});
    }
    setShowNewFlowModal(true);
  };

  const redirectToFlow = (
    flowId: string,
    flowName: string,
    flowData: { [x: string]: any } | undefined
  ) => {
    const clickedFlow = flowData || {};
    const channel = clickedFlow.channel?.toLowerCase();
    const journeyType = clickedFlow.journeyType || "utility";

    const { startDate, endDate } = getLast7DaysStartAndEndDate();

    dispatcher(
      homeDataAction.addState({
        flowName: flowName,
        storeId: storeId,
        flowId: flowId,
        type: type,
        channel: channel,
        userData: {} as userDataState,
        journeyType,
        analyticsData: {
          startDate,
          endDate,
          shimmer: true,
        },
      })
    );
    if (channel) {
      navigate(`/${storeId}/${flowId}/${channel}/analytics`);
    } else navigate("/404");
  };

  const addFlow = async (
    flowName: string,
    description: string,
    channel: string,
    journeyType: string,
    themes: string[],
    subThemes: string[],
    otherCategories: string[],
    domain?: string
  ) => {
    setFlowLoading(true);
    const dndStatus = journeyType === "marketing" ? true : false;
    const flowData: any = {
      flowName: flowName,
      description: description,
      categoryTheme: themes,
      categorySubTheme: subThemes,
      categoryType: otherCategories,
      channel: channel,
      journeyType: journeyType,
      dndEnabled: dndStatus,
      ...(domain ? { domain } : {}),
    };
    const resp = await createNewFlow(storeId, flowData);
    setFlowLoading(false);
    if (!resp) return;
    const flowId = resp.flow_id;
    flowIdExistingMap[flowId] = "new";
    redirectToFlow(flowId, flowName, flowData);
    setShowNewFlowModal(false);
    props.emitFlowName(flowName);
    dispatcher(
      homeDataAction.addState({
        storeId: storeId,
        flowName: flowName,
        flowId: flowId,
        type: type,
        channel: channel,
        userData: {} as userDataState,
        journeyType: journeyType as "marketing" | "utility" | "AI Based" | "Non AI Based",
      })
    );
  };

  const onClickEdit = async (
    flowName: string,
    description: string,
    channel: string,
    journeyType: string,
    themes: string[],
    subThemes: string[],
    otherCategories: string[]
  ) => {
    const dndStatus = journeyType === "marketing" ? true : false;
    itemList[currentId].flowName = flowName;
    itemList[currentId].description = description;
    itemList[currentId].journeyType = journeyType;
    itemList[currentId].categoryTheme = themes;
    itemList[currentId].categorySubTheme = subThemes;
    itemList[currentId].categoryType = otherCategories;
    itemList[currentId].dndEnabled = dndStatus;
    await superAdminFns.saveFlowMeta(storeId, currentId, itemList);
    setShowEditFlowModal(false);
  };

  const selectFlows = (flowId: string) => {
    let selectedFlowsUpdated = { ...selectedFlows };
    selectedFlowsUpdated[flowId] = !selectedFlowsUpdated[flowId] || false;
    setSelectedFlows(selectedFlowsUpdated);
    const selectedFlowIds = Object.keys(selectedFlowsUpdated).filter(
      (flowId) => {
        return selectedFlowsUpdated[flowId];
      }
    );
    setCurrentSelectedFlows(selectedFlowIds);
  };

  const clickPublish = () => {
    const selectedFlowIds = Object.keys(selectedFlows).filter((flowId) => {
      return selectedFlows[flowId];
    });
    if (selectedFlowIds.length === 0) return;
    setShowPublishModal(true);
  };

  const onPublishToStores = async (storeIds: string[]) => {
    setLoading(true);
    const errorString = await superAdminFns.duplicateToStores(
      storeIds,
      currentSelectedFlows,
      storeId
    );
    if (errorString) {
      dispatcher(
        notificationDataStateActions.setNotifications({
          successMessage: "",
          errorMessage: errorString,
        })
      );
      setLoading(false);
      return;
    }
    dispatcher(
      notificationDataStateActions.setNotifications({
        successMessage: "Copied successfully",
        errorMessage: "",
      })
    );
    setLoading(false);
    setShowPublishModal(false);
  };

  const onFileUpload = async (fileMetaData: any) => {
    itemList[currentId].mediaName = fileMetaData.mediaName;
    itemList[currentId].mediaType = fileMetaData.mediaType;
    itemList[currentId].mediaSize = fileMetaData.mediaSize;
    itemList[currentId].mediaUrl = fileMetaData.mediaUrl;
    await superAdminFns.saveFlowMeta(storeId, currentId, itemList);
    setItemList(flowData);
    props?.emitUpdatedFlowList?.(flowData);
    setModalTypeAndValue();
  };

  const modalSelected = () => {
    switch (type) {
      case "TRIGGER":
        return (
          <Modal>
            <TriggerModal
              onClickCancel={setModalTypeAndValue}
              onClickSave={onClickSaveTrigger}
            />
          </Modal>
        );

      case "UPLOAD":
        return (
          <Modal>
            <UploadFile
              flowId={currentId}
              onClickCancel={setModalTypeAndValue}
              onFileUpload={onFileUpload}
            />
          </Modal>
        );

      case "ENABLEPODS":
        return (
          <Modal>
            <PodEnableModal
              onClickCancel={setModalTypeAndValue}
              enabledPods={itemList[currentId]?.enabledPods}
              onClickSave={onClickSaveEnablements}
            />
          </Modal>
        );
    }
  };

  const actionsList: PopUpOptions[] = [
    {
      eventName: "Edit",
      eventHandler: () =>
        redirectToFlow(
          currentId,
          itemList?.[currentId]?.flowName,
          itemList?.[currentId]
        ),
    },
  ];

  return (
    <>
      {showNewFlowModal && (
        <Modal>
          <NewFlowModal
            loading={flowLoading}
            addFlow={addFlow}
            cancel={() => setShowNewFlowModal(false)}
            isNewFlow={true}
            storeId={storeId}
          />
        </Modal>
      )}
      {showEditFlowModal && (
        <Modal>
          <NewFlowModal
            loading={false}
            addFlow={onClickEdit}
            cancel={() => setShowEditFlowModal(false)}
            flowName={itemList[currentId].flowName}
            description={itemList[currentId].description}
            journeyType={itemList[currentId].journeyType}
            storeId={storeId}
            themes={itemList[currentId].categoryTheme ?? []}
            otherCategories={itemList[currentId].categoryType ?? []}
            subThemes={itemList[currentId].categorySubTheme ?? []}
          />
        </Modal>
      )}
      {showPublishModal && (
        <Modal>
          <PublishModal
            flowIds={currentSelectedFlows}
            storeIdsArray={[]}
            onClickCancel={() => setShowPublishModal(false)}
            onClickPublish={onPublishToStores}
            loading={loading}
          />
        </Modal>
      )}
      <div className="right__header">
        <div className="header__input">
          {/* Search Bar */}
          <div>
            <input
              type="text"
              placeholder="Search by flow name or keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "300px",
                padding: "8px 12px",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>
        <div className="header__buttons">
          {currentSelectedFlows.length > 0 && (
            <Button
              buttonText="Publish to Store"
              buttonType="secondary"
              onClick={clickPublish}
            />
          )}
          <Button
            buttonText="Create new Flow"
            buttonType="secondary"
            onClick={createFlow}
            LeadingIcon={() => (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PlusIcon width={20} height={20} />
              </div>
            )}
          />
        </div>
      </div>
      {itemList && !!Object.keys(itemList).length ? (
        <TableStyle>
          {showModal && modalSelected()}
          <div className="table-wrapper">
            <table className="flow-table">
              <colgroup>
                <col style={{ minWidth: "48px" }} />
                <col style={{ width: "200px", maxWidth: "200px" }} />{" "}
                {/* Reduced flow name width */}
                <col style={{ minWidth: "80px" }} />
                <col style={{ minWidth: "120px" }} />
                <col style={{ minWidth: "80px" }} />
                <col style={{ minWidth: "80px" }} />
                <col style={{ minWidth: "80px" }} />
              </colgroup>
              <thead>
                <tr>
                  <th></th>
                  <th>Flow name</th>
                  <th>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <span>Trigger</span>
                      <select
                        value={triggerFilter}
                        onChange={(e) => setTriggerFilter(e.target.value)}
                        style={{
                          padding: "4px 6px",
                          border: "1px solid #e0e0e0",
                          borderRadius: "4px",
                          fontSize: "12px",
                          backgroundColor: "white",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {triggerOptions.map((option) => (
                          <option key={option} value={option}>
                            {option === "all" ? "All" : option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </th>
                  <th>Subtrigger</th>
                  <th>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <span>Status</span>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={{
                          padding: "4px 6px",
                          border: "1px solid #e0e0e0",
                          borderRadius: "4px",
                          fontSize: "12px",
                          backgroundColor: "white",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {statusOptions.map((option) => (
                          <option key={option} value={option}>
                            {option === "all"
                              ? "All"
                              : option === "enabled"
                                ? "Enabled"
                                : option === "disabled"
                                  ? "Disabled"
                                  : option === "test"
                                    ? "Test"
                                    : option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </th>
                  <th>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <span>Channel type</span>
                      <select
                        value={channelFilter}
                        onChange={(e) => setChannelFilter(e.target.value)}
                        style={{
                          padding: "4px 6px",
                          border: "1px solid #e0e0e0",
                          borderRadius: "4px",
                          fontSize: "12px",
                          backgroundColor: "white",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {channelOptions.map((option) => (
                          <option key={option} value={option}>
                            {option === "all" ? "All" : option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </th>
                  <th></th>
                </tr>
              </thead>

              {filteredItems.map((key, index) => {
                const value = itemList[key];
                return (
                  <tbody key={key}>
                    <tr
                      onClick={(event) => {
                        redirectToFlow(key, value?.flowName, value);
                        event.stopPropagation();
                      }}
                    >
                      <td className="checkbox-length">
                        <input
                          type="checkbox"
                          checked={selectedFlows[key] || false}
                          onClick={(event) => {
                            selectFlows(key);
                            event.stopPropagation();
                          }}
                        />
                      </td>
                      <td
                        className="description-length"
                        onClick={(event) => {
                          onClickHandle(key, true);
                          setShowEditFlowModal(true);
                          event.stopPropagation();
                        }}
                        style={{
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <div
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {value.flowName}
                        </div>
                        <div
                          className="description-text"
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {value.description}
                        </div>
                      </td>
                      <td
                        onClick={(event) => {
                          onClickHandle(key, true);
                          setModalTypeAndValue("TRIGGER");
                          event.stopPropagation();
                        }}
                      >
                        {value.triggerType}
                      </td>
                      <td>
                        {value.triggerType?.toLowerCase() === "keyword" ? (
                          <div
                            style={{
                              maxWidth: "120px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {keywordsList[key]?.length > 0 ? (
                              <span title={keywordsList[key]?.join(", ")}>
                                {keywordsList[key]?.join(", ")}
                              </span>
                            ) : (
                              "-"
                            )}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        <div style={{ width: "fit-content" }}>
                          {value.isTestMode ? (
                            <Tag tagText={"Test Mode"} type={"warning"} />
                          ) : (
                            <Tag
                              tagText={value.status ? "Enabled" : "Disabled"}
                              type={value.status ? "positive" : "negative"}
                            />
                          )}
                        </div>
                      </td>
                      <td onClick={(event) => event.stopPropagation()}>
                        {value.channel}
                      </td>

                      <td className="more-length">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            position: "relative",
                          }}
                        >
                          <div
                            style={{ display: "flex", width: 24 }}
                            onClick={(event) => {
                              onClickHandle(key);
                              event.stopPropagation();
                            }}
                          >
                            <span
                              style={{
                                margin: "auto",
                                display: "flex",
                              }}
                            >
                              <OptionsIcon />
                            </span>
                            {showPopover === key && (
                              <div>
                                <BotPopup
                                  list={actionsList}
                                  rightPosition={"0px"}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                );
              })}
            </table>
          </div>
        </TableStyle>
      ) : (
        <NoFlow>
          <p>Click on create new flow button to start building flow!</p>
        </NoFlow>
      )}
    </>
  );
};

export default TemplateTable;
